<template>
  <div class="min-h-[calc(100vh-148px)] md:min-h-0 pb-48 md:pb-96" @keydown.enter="handleEnterKey">
    <div v-if="showBreadcrumb" :class="breadcrumbContainerClass">
      <Breadcrumb :header="backButtonLabel" :is-button="true" class="mt-16 mb-24 md:mt-0" @click="navigateToPreviousStep" />
    </div>
    <div v-show="!loading">
      <router-view v-slot="{ Component }">
        <keep-alive>
          <component :is="Component" v-if="Component" :key="$route.name" :steps-data="stepsData" :draft="draftData" />
        </keep-alive>
      </router-view>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { RouteConfig } from 'vue-router';
import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import { onRegisterNextButton } from '@/helpers/routeStepperNextButtonBus';
import { RegisteredRouteStepperNextButton } from '@/components/route_stepper/types';

interface RouteStepperData {
  loading: boolean;
  stepsData: Record<string, any>[];
  draftData: Record<string, any> | null;
  registeredRouteStepperNextButton: RegisteredRouteStepperNextButton | null;
  isFirstModelValueRun: boolean;
  isResubmitting: boolean;
  unregisterNextButton: (() => void) | null;
}

export default {
  name: 'RouteStepper',
  components: { Breadcrumb },
  provide() {
    return {
      navigateToNextStep: this.navigateToNextStep,
    };
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    modelValue: {
      type: Object as PropType<Record<string, any> | null>,
      default: () => ({}),
    },
    stepFilter: {
      type: Function,
      default: null,
    },
    hideBreadcrumb: {
      type: Boolean,
      default: false,
    },
    breadcrumbContainerClass: {
      type: String,
      default: null,
    },
    autoValidateOnInit: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit', 'update:modelValue', 'progress'],
  data(): RouteStepperData {
    return {
      loading: false,
      stepsData: [],
      draftData: {},
      registeredRouteStepperNextButton: null,
      isFirstModelValueRun: true,
      isResubmitting: false,
      unregisterNextButton: null,
    };
  },
  computed: {
    backButtonLabel(): string {
      const breadcrumbLabel = this.$route.meta?.routeStepper?.breadcrumbLabel;

      if (breadcrumbLabel) {
        return breadcrumbLabel;
      }

      return this.previousStep?.meta?.routeStepper?.stepLabel || 'Previous Step';
    },
    steps(): RouteConfig[] {
      const parentPath = this.$route?.matched?.[0]?.path ?? null;
      const parentRoute = this.$router?.options?.routes?.find((route) => route.path === parentPath);

      if (!parentRoute?.children) {
        console.error('No children routes found within parent route');
        return [];
      }

      let steps = parentRoute?.children?.filter((child: any) => !!child.component);

      if (typeof this.stepFilter === 'function') {
        steps = steps.filter((step) => this.stepFilter(step));
      }

      steps = steps.filter((step) => !this.isStepDisabled(step));

      return steps;
    },
    currentStepIndex(): number {
      const currentStepIndex = this.steps.findIndex((step) => step.name === this.$route.name);
      if (currentStepIndex === -1) {
        console.error('Current step not found within steps');
        return 0; // default to the first step if not found
      }
      return currentStepIndex;
    },
    isEditStep(): boolean {
      return this.$route.query.mode === 'edit';
    },
    nextStep(): RouteConfig | null {
      return this.steps[this.currentStepIndex + 1] || null;
    },
    previousStep(): RouteConfig | null {
      return this.steps[this.currentStepIndex - 1] || null;
    },
    returnPath(): string | null {
      const returnPath: string | null = this.$route.query.return_path as string | null;
      return returnPath;
    },
    showBreadcrumb(): boolean {
      if (this.hideBreadcrumb || this.isEditStep || this.currentStepIndex <= 0) {
        return false;
      }

      return !this.$route.meta?.routeStepper?.hideBreadcrumb;
    },
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler() {
        this.$emit('progress', {
          // emit up the current step index and total steps for progress tracking
          currentStep: this.currentStepIndex + 1,
          totalSteps: this.steps.length,
        });
      },
    },
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.scrollToTop();
      },
    },
    modelValue: {
      immediate: true,
      handler(modelValue: Record<string, any> | null): void {
        if (!modelValue || Object.keys(modelValue).length === 0) {
          return;
        }

        if (modelValue !== this.draftData) {
          this.draftData = modelValue;

          if (!this.isFirstModelValueRun || this.autoValidateOnInit) {
            this.resubmitEachStep();
          }
        }

        this.isFirstModelValueRun = false;
      },
    },
  },
  created() {
    this.navigateToFirstIncompleteOrLastStep();
    this.unregisterNextButton = onRegisterNextButton((instance) => {
      this.registeredRouteStepperNextButton = instance as RegisteredRouteStepperNextButton;
    });
  },
  beforeUnmount() {
    this.unregisterNextButton?.();
    this.stepsData = [];
  },
  methods: {
    isStepDisabled(step: RouteConfig): boolean {
      return (step.meta as { routeStepper?: { disabled?: boolean } } | undefined)?.routeStepper?.disabled === true;
    },
    async handleEnterKey(event: KeyboardEvent) {
      const el = event.target as HTMLElement | null;
      const isInsideForm = el?.closest?.('form');

      // if the enter key is pressed inside a form and a next button is registered, submit the step
      if (isInsideForm && this.registeredRouteStepperNextButton !== null) {
        event.preventDefault();
        await this.registeredRouteStepperNextButton.submitStep();
      }
    },
    scrollToTop() {
      // double timeout needed for weirdness around mobile device scrolling limitations
      setTimeout(() => {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'auto' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
          window.scroll(0, 0);
        }, 0);
      }, 0);
    },
    async resubmitEachStep() {
      if (this.isResubmitting) {
        return;
      }

      this.isResubmitting = true;
      this.loading = true;

      try {
        for (const _ of this.steps) {
          await this.$nextTick();

          const curStepButton = this.registeredRouteStepperNextButton;
          if (!curStepButton) {
            break;
          }

          this.registeredRouteStepperNextButton = null;
          const curStepValid = await curStepButton.submitStep({ emitErrors: false });

          if (!curStepValid) {
            break;
          }
        }
      } finally {
        this.loading = false;
        this.isResubmitting = false;
      }
    },
    navigateToFirstIncompleteOrLastStep(): void {
      const firstIncompleteStep = this.steps.find((_, index) => {
        return !this.stepsData[index];
      });
      const targetStep = firstIncompleteStep || this.steps[this.steps.length - 1]; // revert to final step if all are complete

      if (targetStep === this.steps[this.currentStepIndex]) return; // do not navigate if already on the target step

      this.$router.push({ name: targetStep.name });
    },
    saveStepData(newStepData: Record<string, any> = {}): void {
      const newStepsData = [...this.stepsData];
      newStepsData[this.currentStepIndex] = {
        ...newStepsData[this.currentStepIndex],
        ...newStepData,
      };
      this.stepsData = newStepsData;
    },
    saveDraftData(newDraftData: Record<string, any>): void {
      this.draftData = { ...this.draftData, ...newDraftData };
      this.$emit('update:modelValue', this.draftData);
    },
    async navigateToPreviousStep(): Promise<void> {
      if (!this.previousStep) {
        console.error('No previous step found');
        return;
      }
      await this.$router.push({ name: this.previousStep.name });
    },
    async navigateToNextStep(newStepData?: Record<string, any>, newDraftData: Record<string, any> = {}): Promise<void> {
      if (!this.nextStep) {
        // on the final step, emit a submit event with the draft data
        this.$emit('submit', this.draftData);
        return;
      }

      this.saveStepData(newStepData);
      this.saveDraftData(newDraftData);

      // if step is edit, return to the review page
      if (this.isEditStep && this.returnPath) {
        await this.$router.push(this.returnPath);
        return;
      }

      await this.$router.push({ name: this.nextStep.name });
    },
  },
};
</script>
